/* KLAIM Search Lab: fresh fullscreen search behavior. */
(() => {
  const overlay = document.querySelector('[data-klaim-search-overlay]');
  const trigger = document.querySelector('[data-klaim-search-trigger]');
  if (!overlay || !trigger) return;

  const surface = overlay.querySelector('.klaim-search-overlay__surface');
  const scrollArea = overlay.querySelector('.klaim-search-overlay__scroll');
  const input = overlay.querySelector('[data-klaim-search-input]');
  const form = overlay.querySelector('[data-klaim-search-form]');
  const clearButton = overlay.querySelector('[data-klaim-search-clear]');
  const announcementHost = overlay.querySelector('[data-klaim-search-announcement]');
  const menuButton = overlay.querySelector('[data-klaim-search-menu]');
  const defaultContent = overlay.querySelector('[data-klaim-search-default]');
  const results = overlay.querySelector('[data-klaim-search-results]');
  const resultsGrid = overlay.querySelector('[data-klaim-search-results-grid]');
  const resultCount = overlay.querySelector('[data-klaim-search-result-count]');
  const viewAll = overlay.querySelector('[data-klaim-search-view-all]');
  const emptyState = overlay.querySelector('[data-klaim-search-empty]');
  const recentSection = overlay.querySelector('[data-klaim-search-recent-section]');
  const recentList = overlay.querySelector('[data-klaim-search-recent-list]');
  const resetButton = overlay.querySelector('[data-klaim-search-reset]');
  const personalGrid = overlay.querySelector('[data-klaim-search-personal-grid]');
  const personalNote = overlay.querySelector('[data-klaim-search-personal-note]');
  const termsKey = 'klaim-search-overlay-terms-v2';
  const viewedKey = 'klaim-search-overlay-viewed-v2';
  let requestId = 0;
  let debounceTimer;
  let previousFocus;
  let previousScrollY = 0;
  let hiddenStickyState = [];
  const headerPanels = window.KlaimHeaderPanels;
  overlay.inert = overlay.getAttribute('aria-hidden') !== 'false';

  const hideUnderlyingLayers = () => {
    hiddenStickyState = [...document.querySelectorAll('.klaim-sticky-atc')].map((element) => ({
      element,
      display: element.style.getPropertyValue('display'),
      displayPriority: element.style.getPropertyPriority('display'),
      ariaHidden: element.getAttribute('aria-hidden')
    }));
    hiddenStickyState.forEach(({ element }) => {
      element.style.setProperty('display', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    });
    document.querySelector('#hdrSearchDrawer')?.classList.remove('open');
    document.querySelector('#searchBg')?.classList.remove('open');
    document.documentElement.classList.remove('search-drawer-open');
    document.body.classList.remove('search-drawer-open');
  };

  const restoreUnderlyingLayers = () => {
    hiddenStickyState.forEach(({ element, display, displayPriority, ariaHidden }) => {
      if (display) element.style.setProperty('display', display, displayPriority);
      else element.style.removeProperty('display');
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    hiddenStickyState = [];
  };

  const syncAnnouncement = () => {
    if (!announcementHost) return;
    const source = document.querySelector('#HdrAnnouncementBar');
    announcementHost.replaceChildren();
    if (!source) {
      announcementHost.hidden = true;
      return;
    }
    const clone = source.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('hidden');
    clone.setAttribute('aria-hidden', 'false');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.removeAttribute('style');
    announcementHost.appendChild(clone);
    announcementHost.hidden = false;
  };

  const read = (key) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (error) {}
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[character]));

  const searchUrl = (term) => `/search?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;

  const saveTerm = (value) => {
    const term = value.trim();
    if (!term) return;
    const terms = [term, ...read(termsKey).filter((item) => item.toLowerCase() !== term.toLowerCase())].slice(0, 6);
    write(termsKey, terms);
    renderRecentTerms();
  };

  const renderRecentTerms = () => {
    const terms = read(termsKey);
    if (!recentSection || !recentList) return;
    recentSection.hidden = terms.length === 0;
    recentList.innerHTML = terms.map((term) => `<a class="klaim-search-overlay__recent-term" href="${searchUrl(term)}">${escapeHtml(term)}</a>`).join('');
  };

  const productCard = (product) => {
    if (!product || !product.url || !product.image) return '';
    const title = product.title || 'Product';
    const vendor = product.vendor || 'KLAIM';
    const price = product.price || '';
    return `<article class="klaim-search-overlay__product" data-klaim-search-product data-product-url="${escapeHtml(product.url)}" data-product-title="${escapeHtml(title)}" data-product-vendor="${escapeHtml(vendor)}" data-product-price="${escapeHtml(price)}" data-product-image="${escapeHtml(product.image)}">
        <div class="klaim-search-overlay__product-media">
          <a class="klaim-search-overlay__product-image-link" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(title)}" data-klaim-search-product-link>
            <img src="${escapeHtml(product.image)}" alt="${escapeHtml(title)}" width="720" height="720" loading="lazy">
          </a>
          <button class="klaim-search-overlay__product-plus kqa-trigger" type="button" data-kqa-trigger data-product-url="${escapeHtml(product.url)}" data-product-title="${escapeHtml(title)}" aria-haspopup="dialog" aria-label="Quick add ${escapeHtml(title)}">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" /></svg>
          </button>
        </div>
      <div class="klaim-search-overlay__product-copy">
        <span class="klaim-search-overlay__product-vendor">${escapeHtml(vendor)}</span>
        <a class="klaim-search-overlay__product-title" href="${escapeHtml(product.url)}" data-klaim-search-product-link>${escapeHtml(title)}</a>
        <span class="klaim-search-overlay__product-price">${escapeHtml(price)}</span>
      </div>
    </article>`;
  };

  const renderPersonalization = () => {
    const viewed = read(viewedKey).filter((item) => item.url && item.image);
    if (!personalGrid || !viewed.length) {
      if (personalNote) personalNote.hidden = true;
      return;
    }
    const unique = viewed.filter((item, index, list) => list.findIndex((candidate) => candidate.url === item.url) === index).slice(0, 1);
    personalGrid.innerHTML = unique.map(productCard).join('');
    if (personalNote) {
      personalNote.hidden = false;
      personalNote.textContent = 'Picked from your recent visits';
    }
  };

  const productFromApi = (product) => {
    const image = typeof product.image === 'string'
      ? product.image
      : product.image?.url || product.featured_image?.url || product.featured_image;
    return {
      title: product.title,
      vendor: product.vendor,
      url: product.url,
      image,
      price: product.price || product.price_min || ''
    };
  };

  const renderResults = (products) => {
    if (!resultsGrid || !resultCount || !emptyState) return;
    const visible = products.filter((product) => product.url && product.image).slice(0, 10);
    resultsGrid.innerHTML = visible.map(productCard).join('');
    resultCount.textContent = visible.length ? `${visible.length} shown` : '';
    emptyState.hidden = visible.length > 0;
  };

  const fetchResults = async (term) => {
    const currentRequest = ++requestId;
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'query,product',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'hide'
    });
    try {
      const response = await fetch(`/search/suggest.json?${params.toString()}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search request failed');
      const data = await response.json();
      if (currentRequest !== requestId) return;
      const products = (data.resources?.results?.products || []).map(productFromApi);
      renderResults(products);
    } catch (error) {
      if (currentRequest !== requestId) return;
      renderResults([]);
    }
  };

  const updateQueryState = () => {
    requestId += 1;
    const term = input?.value.trim() || '';
    const hasTerm = term.length > 0;
    if (clearButton) clearButton.hidden = !hasTerm;
    if (defaultContent) defaultContent.hidden = hasTerm;
    if (results) results.hidden = !hasTerm;
    if (viewAll) {
      viewAll.hidden = !hasTerm;
      viewAll.href = searchUrl(term);
    }
    if (!hasTerm) {
      if (resultsGrid) resultsGrid.innerHTML = '';
      if (resultCount) resultCount.textContent = '';
      if (emptyState) emptyState.hidden = true;
    }
    return term;
  };

  const open = () => {
    if (overlay.classList.contains('is-open')) return;
    previousFocus = document.activeElement;
    previousScrollY = window.scrollY;
    headerPanels?.activate('search', trigger);
    if (!headerPanels) syncAnnouncement();
    hideUnderlyingLayers();
    document.documentElement.classList.add('klaim-search-overlay-open');
    document.body.classList.add('klaim-search-overlay-open');
    overlay.classList.add('is-open');
    overlay.inert = false;
    overlay.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    renderRecentTerms();
    renderPersonalization();
    window.requestAnimationFrame(() => {
      if (overlay.classList.contains('is-open')) input?.focus({ preventScroll: true });
    });
  };

  const close = () => {
    if (!overlay.classList.contains('is-open')) return;
    overlay.inert = true;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('klaim-search-overlay-open');
    document.body.classList.remove('klaim-search-overlay-open');
    restoreUnderlyingLayers();
    if (headerPanels) headerPanels.deactivate('search');
    else {
      window.scrollTo(0, previousScrollY);
      previousFocus?.focus?.({ preventScroll: true });
    }
  };

  headerPanels?.register('search', { element: overlay, close });

  const rememberProduct = (card) => {
    if (!card) return;
    const item = {
      url: card.dataset.productUrl,
      title: card.dataset.productTitle,
      vendor: card.dataset.productVendor,
      price: card.dataset.productPrice,
      image: card.dataset.productImage || card.querySelector('img')?.currentSrc || card.querySelector('img')?.src
    };
    if (!item.url || !item.image) return;
    write(viewedKey, [item, ...read(viewedKey).filter((candidate) => candidate.url !== item.url)].slice(0, 8));
  };

  trigger.addEventListener('click', () => {
    if (overlay.classList.contains('is-open')) close();
    else open();
  });

  overlay.addEventListener('click', (event) => {
    if (event.target.closest('[data-klaim-search-close]')) close();
    const productLink = event.target.closest('[data-klaim-search-product-link]');
    if (productLink) rememberProduct(productLink.closest('[data-klaim-search-product]'));
  });

  input?.addEventListener('input', () => {
    window.clearTimeout(debounceTimer);
    const term = updateQueryState();
    if (term.length < 1) return;
    debounceTimer = window.setTimeout(() => fetchResults(term), 220);
  });

  clearButton?.addEventListener('click', () => {
    input.value = '';
    updateQueryState();
    input.focus({ preventScroll: true });
  });

  menuButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    close();
    window.setTimeout(() => document.querySelector('[data-klaim-mobile-menu-lab-trigger]')?.click(), 0);
  });

  resetButton?.addEventListener('click', () => {
    write(termsKey, []);
    renderRecentTerms();
  });

  form?.addEventListener('submit', (event) => {
    const term = input?.value.trim() || '';
    if (!term) {
      event.preventDefault();
      updateQueryState();
      return;
    }
    window.clearTimeout(debounceTimer);
    requestId += 1;
    saveTerm(term);
  });

  viewAll?.addEventListener('click', () => saveTerm(input?.value || ''));

  document.addEventListener('keydown', (event) => {
    if (headerPanels) return;
    if (!overlay.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab' || !surface) return;
    const focusable = [...surface.querySelectorAll('a[href], button:not([hidden]), input:not([hidden])')].filter((element) => !element.disabled && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  renderRecentTerms();
  renderPersonalization();
  updateQueryState();
})();
